import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Download, Printer, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const RANGE_OPTS = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["month", "This month"], ["year", "This year"], ["custom", "Custom"]];
const SOURCE_OPTS = [["all", "All sources"], ["native", "HiredToday native"], ["adzuna", "Adzuna"], ["reed", "Reed"], ["govuk", "GOV.UK Apprenticeships"]];
const CHANNEL_OPTS = [["", "All channels"], ["Google", "Google"], ["Direct", "Direct"], ["Social", "Social"], ["Email", "Email"], ["Referral", "Referral"], ["Campaign", "Campaign"]];
const DEVICE_OPTS = [["", "All devices"], ["Desktop", "Desktop"], ["Mobile", "Mobile"], ["Tablet", "Tablet"]];
const TYPE_OPTS = ["", "Full-time", "Part-time", "Contract", "Temporary"];
const GRAN_OPTS = [["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]];

const fmtN = (n) => (n ?? 0).toLocaleString("en-GB");
const gbp = (p) => `£${((p ?? 0) / 100).toFixed(2)}`;

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="font-display text-2xl font-bold text-zinc-950 mt-1">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function TopList({ title, rows, testid }) {
  return (
    <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">{title}</p>
      {rows && rows.length ? (
        <ul className="space-y-1.5">
          {rows.slice(0, 8).map((r) => (
            <li key={r.label} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-zinc-800 truncate">{r.label}</span>
              <span className="text-zinc-500 font-semibold shrink-0">{fmtN(r.count ?? (r.views + r.clicks + r.applications))}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-zinc-400">No data in this period.</p>
      )}
    </div>
  );
}

function bucketTrend(trend, gran) {
  if (gran === "daily") return trend;
  const map = new Map();
  for (const t of trend) {
    const d = new Date(t.date + "T00:00:00Z");
    let key;
    if (gran === "monthly") {
      key = t.date.slice(0, 7);
    } else {
      const day = (d.getUTCDay() + 6) % 7;
      const mon = new Date(d); mon.setUTCDate(d.getUTCDate() - day);
      key = mon.toISOString().slice(0, 10);
    }
    if (!map.has(key)) map.set(key, { date: key, visitors: 0, impressions: 0, views: 0, clicks: 0, applications: 0 });
    const b = map.get(key);
    b.visitors += t.visitors; b.impressions += t.impressions; b.views += t.views; b.clicks += t.clicks; b.applications += t.applications;
  }
  return [...map.values()];
}

export default function TrafficAnalytics() {
  const [filters, setFilters] = useState({ range: "30d", from: "", to: "", job_source: "all", company: "", job_id: "", location: "", category: "", job_type: "", channel: "", device: "" });
  const [data, setData] = useState(null);
  const partnerHealth = useMemo(() => (data?.partners || [data?.partner]).filter(Boolean), [data]);
  const [loading, setLoading] = useState(true);
  const [gran, setGran] = useState("daily");
  const [partners, setPartners] = useState(null);
  const [scopes, setScopes] = useState({ adzuna: { text: "", pages: 2 }, reed: { text: "", pages: 2 }, govuk: { text: "", pages: 5 } });
  const [savingCfg, setSavingCfg] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [syncing, setSyncing] = useState({});

  const params = () => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k === "from" ? "from_" : k] = v; });
    return p;
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/analytics/traffic", { params: params() })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  // reports refresh automatically while the tab is open
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const sc = (s, dflt) => ({ text: (s?.queries || []).join("\n"), pages: s?.max_pages || dflt });
    api.get("/admin/partners").then((r) => {
      setPartners(r.data);
      setScopes({ adzuna: sc(r.data.adzuna_scope, 2), reed: sc(r.data.reed_scope, 2), govuk: sc(r.data.govuk_scope, 5) });
    }).catch(() => {});
  }, []);

  const syncNow = async (key) => {
    setSyncing((s) => ({ ...s, [key]: true }));
    try {
      const r = await api.post(`/admin/partners/${key}/sync`);
      if (r.data?.queued === false) {
        toast.info("A sync is already running, figures refresh automatically");
      } else {
        toast.success("Sync started, figures refresh automatically");
      }
      setTimeout(() => { load(); setSyncing((s) => ({ ...s, [key]: false })); }, 20000);
    } catch (err) {
      toast.error(formatApiError(err));
      setSyncing((s) => ({ ...s, [key]: false }));
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/analytics/export.csv", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hiredtoday-traffic-${filters.range}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const savePartners = async () => {
    setSavingCfg(true);
    try {
      const mk = (k) => ({ queries: (scopes[k]?.text || "").split("\n").map((q) => q.trim()).filter(Boolean), max_pages: scopes[k]?.pages || 2 });
      await api.put("/admin/partners", { partners: partners.partners, adzuna_scope: mk("adzuna"), reed_scope: mk("reed"), govuk_scope: mk("govuk") });
      toast.success("Partner settings saved");
      const fresh = await api.get("/admin/partners");
      setPartners(fresh.data);
      const sc = (s, dflt) => ({ text: (s?.queries || []).join("\n"), pages: s?.max_pages || dflt });
      setScopes({ adzuna: sc(fresh.data.adzuna_scope, 2), reed: sc(fresh.data.reed_scope, 2), govuk: sc(fresh.data.govuk_scope, 5) });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSavingCfg(false); }
  };

  const trend = useMemo(() => bucketTrend(data?.trend || [], gran), [data, gran]);
  const maxTrend = Math.max(1, ...trend.map((t) => Math.max(t.visitors, t.impressions, t.views, t.clicks, t.applications)));
  const adz = partners?.partners?.adzuna || {};
  const setAdz = (k, v) => setPartners((p) => ({ ...p, partners: { ...p.partners, adzuna: { ...p.partners.adzuna, [k]: v } } }));
  const sel = "h-10 px-2 border border-zinc-300 rounded-md text-sm bg-white";
  const inp = "h-10 px-3 border border-zinc-300 rounded-md text-sm";

  return (
    <div className="mt-6" data-testid="traffic-tab">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="font-display text-xl font-bold text-zinc-950">Traffic & partner analytics</h2>
          <p className="text-sm text-zinc-500 mt-1">Server-side tracking across native and partner listings. No personal data is reported.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadCsv} data-testid="traffic-export-csv"
            className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors inline-flex items-center gap-1.5">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
          <button onClick={() => setReportOpen(true)} data-testid="traffic-report-open"
            className="h-10 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors inline-flex items-center gap-1.5">
            <Printer className="h-3.5 w-3.5" /> Partner report / PDF
          </button>
          <button onClick={load} data-testid="traffic-refresh"
            className="h-10 px-3 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="glass rounded-lg p-4 mb-6 flex flex-wrap gap-2 items-end" data-testid="traffic-filters">
        <select value={filters.range} onChange={(e) => setFilters({ ...filters, range: e.target.value })} className={sel} data-testid="traffic-filter-range">
          {RANGE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {filters.range === "custom" && (
          <>
            <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className={inp} data-testid="traffic-filter-from" />
            <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className={inp} data-testid="traffic-filter-to" />
          </>
        )}
        <select value={filters.job_source} onChange={(e) => setFilters({ ...filters, job_source: e.target.value })} className={sel} data-testid="traffic-filter-source">
          {SOURCE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={filters.company} onChange={(e) => setFilters({ ...filters, company: e.target.value })} placeholder="Company" className={inp} data-testid="traffic-filter-company" />
        <input value={filters.job_id} onChange={(e) => setFilters({ ...filters, job_id: e.target.value })} placeholder="Job title" className={inp} data-testid="traffic-filter-job" />
        <input value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} placeholder="Location" className={inp} data-testid="traffic-filter-location" />
        <input value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} placeholder="Category" className={inp} data-testid="traffic-filter-category" />
        <select value={filters.job_type} onChange={(e) => setFilters({ ...filters, job_type: e.target.value })} className={sel} data-testid="traffic-filter-type">
          {TYPE_OPTS.map((v) => <option key={v} value={v}>{v || "All types"}</option>)}
        </select>
        <select value={filters.channel} onChange={(e) => setFilters({ ...filters, channel: e.target.value })} className={sel} data-testid="traffic-filter-channel">
          {CHANNEL_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.device} onChange={(e) => setFilters({ ...filters, device: e.target.value })} className={sel} data-testid="traffic-filter-device">
          {DEVICE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading || !data ? (
        <div className="border border-zinc-200 rounded-md p-10 animate-pulse bg-zinc-50" data-testid="traffic-loading" />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="traffic-kpis">
            <Kpi label="Site visits" value={fmtN(data.visitors.total)} sub={`${fmtN(data.visitors.unique)} unique`} testid="traffic-kpi-visitors" />
            <Kpi label="New vs returning" value={`${fmtN(data.visitors.new)} / ${fmtN(data.visitors.returning)}`} sub="visitors this period" testid="traffic-kpi-returning" />
            <Kpi label="Job impressions" value={fmtN(data.impressions.total)} sub={`${fmtN(data.impressions.unique)} unique visitors`} testid="traffic-kpi-impressions" />
            <Kpi label="Job detail views" value={fmtN(data.views.total)} sub={`${fmtN(data.views.unique)} unique visitors`} testid="traffic-kpi-views" />
            <Kpi label="Outbound apply clicks" value={fmtN(data.outbound_clicks.total)} sub={`${fmtN(data.outbound_clicks.unique)} unique · external only, not completed applications`} testid="traffic-kpi-outbound" />
            <Kpi label="Native applications" value={fmtN(data.applications.total)} sub="confirmed on HiredToday" testid="traffic-kpi-applications" />
            <Kpi label="Registrations" value={fmtN(data.engagement?.registrations)} sub="new accounts this period" testid="traffic-kpi-registrations" />
            <Kpi label="Saved jobs" value={fmtN(data.engagement?.saved_jobs_new)} sub={`${fmtN(data.engagement?.saved_jobs_total)} saved in total`} testid="traffic-kpi-saved" />
            <Kpi label="Job alerts on" value={fmtN(data.engagement?.alerts_active)} sub={`${fmtN(data.engagement?.alerts_new)} new this period`} testid="traffic-kpi-alerts" />
            <Kpi label="Impression → view" value={`${Math.min(data.rates.view_rate, 100)}%`} sub="of impressions opened a job" testid="traffic-kpi-view-rate" />
            <Kpi label="CTR / apply conversion" value={`${data.rates.ctr}% / ${data.rates.application_conversion}%`} sub="outbound CTR · native apply rate" testid="traffic-kpi-ctr" />
          </div>

          <div className="border border-zinc-200 rounded-md bg-white p-4 mb-6" data-testid="traffic-chart">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Trend</p>
              <select value={gran} onChange={(e) => setGran(e.target.value)} className={sel} data-testid="traffic-granularity">
                {GRAN_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {trend.length === 0 ? (
              <p className="text-sm text-zinc-400">No activity in this period yet.</p>
            ) : (
              <div className="flex items-end gap-1.5 h-40">
                {trend.map((t) => (
                  <div key={t.date} className="flex-1 flex items-end justify-center gap-0.5 min-w-0" title={`${t.date}: ${t.views} views · ${t.clicks} clicks · ${t.applications} applications · ${t.visitors} visits`}>
                    <div className="w-2.5 bg-zinc-300 rounded-t" style={{ height: `${Math.max(2, (t.views / maxTrend) * 100)}%` }} />
                    <div className="w-2.5 bg-blue-500 rounded-t" style={{ height: `${Math.max(2, (t.clicks / maxTrend) * 100)}%` }} />
                    <div className="w-2.5 bg-green-500 rounded-t" style={{ height: `${Math.max(2, (t.applications / maxTrend) * 100)}%` }} />
                  </div>
                ))}
              </div>
            )}
            {trend.length === 1 && (
              <p className="text-xs text-zinc-400 mb-2">Only one day in this range so far, the chart fills in as data grows.</p>
            )}
            {trend.length > 0 && (
              <div className="flex justify-between mt-1 text-[10px] text-zinc-400">
                <span>{trend[0].date}</span>
                {trend.length > 2 && <span>{trend[Math.floor(trend.length / 2)].date}</span>}
                {trend.length > 1 && <span>{trend[trend.length - 1].date}</span>}
              </div>
            )}
            <div className="flex gap-4 mt-3 text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-zinc-300" /> Views</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-500" /> Outbound clicks</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-green-500" /> Native applications</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            <TopList title="Top jobs" rows={data.tops.jobs} testid="traffic-top-jobs" />
            <TopList title="Top companies" rows={data.tops.companies} testid="traffic-top-companies" />
            <TopList title="Top locations" rows={data.tops.locations} testid="traffic-top-locations" />
            <TopList title="Top categories" rows={data.tops.categories} testid="traffic-top-categories" />
            <TopList title="Devices" rows={data.tops.devices} testid="traffic-top-devices" />
            <TopList title="Acquisition sources" rows={data.tops.sources} testid="traffic-top-sources" />
            <TopList title="Registration sources" rows={data.engagement?.registrations_by_source} testid="traffic-top-regsources" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="glass rounded-lg p-5" data-testid="partner-health">
              <p className="font-display font-bold text-zinc-950">Partner synchronisation</p>
              {partnerHealth.map((p) => (
                <div key={p.key || "adzuna"} className="mt-3 border-t border-zinc-100 pt-3 first:border-0 first:pt-0" data-testid={`partner-health-${p.key || "adzuna"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-zinc-800">{p.name}</p>
                    <button onClick={() => syncNow(p.key)} disabled={!!syncing[p.key]} data-testid={`partner-sync-${p.key}`}
                      className="h-7 px-3 text-[11px] font-semibold border border-zinc-300 rounded-md hover:border-zinc-500 disabled:opacity-50 transition-colors">
                      {syncing[p.key] ? "Starting…" : "Sync now"}
                    </button>
                  </div>
                  <div className="mt-1.5 space-y-1.5 text-sm text-zinc-600">
                    <p>Last sync: <strong data-testid={`partner-last-sync-${p.key || "adzuna"}`}>{p.last_sync ? `${p.last_sync.status} · ${new Date(p.last_sync.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "never (key not set)"}</strong></p>
                    <p>API calls this period: <strong>{fmtN(p.api_calls_in_range)}</strong> · failed runs: <strong className={p.failed_syncs ? "text-red-600" : ""}>{p.failed_syncs}</strong></p>
                    <p data-testid={`partner-job-stats-${p.key || "adzuna"}`}>Listings, active: <strong>{fmtN(p.jobs.active)}</strong> · new: {fmtN(p.jobs.new)} · updated: {fmtN(p.jobs.updated)} · expired: {fmtN(p.jobs.expired)}</p>
                    {p.errors.length > 0 && (
                      <div className="text-xs text-red-600 mt-1" data-testid={`partner-errors-${p.key || "adzuna"}`}>
                        {p.errors.map((e, i) => <p key={`${e.at || ""}-${e.error}`}>· {e.error}</p>)}
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
                      {p.key === "govuk" ? `${p.name} sync scope (pages 1–10, newest adverts first)` : `${p.name} sync scope (one keyword per line, max 10; pages 1–3)`}
                    </p>
                    {p.key !== "govuk" && (
                      <textarea value={scopes[p.key]?.text ?? ""}
                        onChange={(e) => setScopes((s) => ({ ...s, [p.key]: { ...(s[p.key] || { pages: 2 }), text: e.target.value } }))}
                        rows={3} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid={`traffic-scope-queries-${p.key || "adzuna"}`} />
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <label className="text-sm text-zinc-600">Pages</label>
                      <input type="number" min="1" max={p.key === "govuk" ? 10 : 3} value={scopes[p.key]?.pages ?? 2}
                        onChange={(e) => setScopes((s) => ({ ...s, [p.key]: { ...(s[p.key] || { text: "" }), pages: parseInt(e.target.value || "2", 10) } }))}
                        className="w-20 h-9 px-2 border border-zinc-300 rounded-md text-sm" data-testid={`traffic-scope-pages-${p.key || "adzuna"}`} />
                      <span className="text-xs text-zinc-400">daily sync · quota-safe</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="glass rounded-lg p-5" data-testid="partner-revenue">
              <p className="font-display font-bold text-zinc-950">Partner commercial terms</p>
              <p className="text-xs text-zinc-500 mt-1">For future commercial agreements. Nothing here assumes a partner currently pays us.</p>
              {["adzuna", "reed", "govuk"].map((key) => {
                const cfg = (partners?.partners || {})[key] || {};
                const rev = (data.revenue?.partners || {})[key] || {};
                const setP = (k, v) => setPartners((p) => ({ ...p, partners: { ...p.partners, [key]: { ...(p.partners?.[key] || {}), [k]: v } } }));
                return (
                  <div key={key} className="mt-4 border-t border-zinc-100 pt-4" data-testid={`partner-revenue-${key}`}>
                    <p className="text-sm font-semibold text-zinc-800">{{ adzuna: "Adzuna", reed: "Reed", govuk: "GOV.UK Apprenticeship" }[key]}</p>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <label className="text-xs text-zinc-500">CPC (pence)
                        <input type="number" min="0" value={cfg.cpc_pence ?? 0} onChange={(e) => setP("cpc_pence", parseInt(e.target.value || "0", 10))}
                          className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm" data-testid={`traffic-cpc-${key}`} />
                      </label>
                      <label className="text-xs text-zinc-500">Revenue share %
                        <input type="number" min="0" max="100" value={cfg.revenue_share_pct ?? 0} onChange={(e) => setP("revenue_share_pct", parseInt(e.target.value || "0", 10))}
                          className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm" data-testid={`traffic-share-${key}`} />
                      </label>
                      <label className="text-xs text-zinc-500">Confirmed revenue £
                        <input type="number" min="0" step="0.01" value={((cfg.confirmed_revenue_pence ?? 0) / 100)} onChange={(e) => setP("confirmed_revenue_pence", Math.round(parseFloat(e.target.value || "0") * 100))}
                          className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm" data-testid={`traffic-confirmed-${key}`} />
                      </label>
                    </div>
                    <div className="mt-3 space-y-1 text-sm">
                      <p>Estimated ({fmtN(rev.unique_clicks)} unique clicks × CPC): <strong data-testid={`traffic-revenue-estimated-${key}`}>{gbp(rev.estimated_pence)}</strong> <span className="text-xs text-amber-600 font-semibold">estimated</span></p>
                      <p>Confirmed (entered above): <strong data-testid={`traffic-revenue-confirmed-${key}`}>{gbp(rev.confirmed_pence)}</strong> <span className="text-xs text-green-700 font-semibold">confirmed</span></p>
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-zinc-400 mt-3">Tracked events are shown in the KPIs above. Estimated figures are indicative only.</p>
              <button onClick={savePartners} disabled={savingCfg} data-testid="traffic-partners-save"
                className="mt-4 h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50">
                {savingCfg ? "Saving…" : "Save partner settings"}
              </button>
            </div>
          </div>
        </>
      )}

      {reportOpen && data && (
        <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid="partner-report-overlay">
          <style>{`@media print { body * { visibility: hidden; } #partner-report, #partner-report * { visibility: visible; } #partner-report { position: absolute; inset: 0; width: 100%; background: white; padding: 24px; } }`}</style>
          <div className="max-w-3xl mx-auto my-8 bg-white rounded-lg shadow-xl p-8" id="partner-report" data-testid="partner-report">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-display text-2xl font-bold text-zinc-950">Hired<span className="text-blue-600">Today</span>, Partner Report</p>
                <p className="text-sm text-zinc-500 mt-1">Reporting period: {data.window.start.slice(0, 10)} to {data.window.end.slice(0, 10)} · Generated {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                <p className="text-xs text-zinc-400 mt-0.5">Filters: {Object.entries(data.filters).map(([k, v]) => `${k}=${v}`).join("; ") || "none"}</p>
              </div>
              <button onClick={() => setReportOpen(false)} className="text-zinc-400 hover:text-zinc-700" data-testid="partner-report-close"><X className="h-5 w-5" /></button>
            </div>

            <h3 className="font-display font-bold text-zinc-950 mt-6 mb-2">Key metrics</h3>
            <table className="w-full text-sm border border-zinc-200" data-testid="report-kpis">
              <tbody>
                {[
                  ["Unique HiredToday visitors", fmtN(data.visitors.unique)],
                  ["Total site visits", fmtN(data.visitors.total)],
                  ["Partner job impressions", fmtN(data.impressions.external ?? data.impressions.total)],
                  ["Job detail views", fmtN(data.views.total)],
                  ["Outbound apply clicks (total)", fmtN(data.outbound_clicks.total)],
                  ["Outbound apply clicks (unique)", fmtN(data.outbound_clicks.unique)],
                  ["Outbound CTR", `${data.rates.ctr}%`],
                  ["Native applications (confirmed)", fmtN(data.applications.total)],
                  ["Estimated partner revenue", gbp(data.revenue.estimated_pence)],
                  ["Confirmed partner revenue", gbp(data.revenue.confirmed_pence)],
                ].map(([k, v]) => (
                  <tr key={k} className="border-b border-zinc-100">
                    <td className="px-3 py-2 text-zinc-600">{k}</td>
                    <td className="px-3 py-2 font-semibold text-zinc-950 text-right">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <TopList title="Top jobs" rows={data.tops.jobs} testid="report-top-jobs" />
              <TopList title="Top categories" rows={data.tops.categories} testid="report-top-categories" />
              <TopList title="Top UK locations" rows={data.tops.locations} testid="report-top-locations" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <TopList title="Devices" rows={data.tops.devices} testid="report-top-devices" />
              <TopList title="Acquisition sources" rows={data.tops.sources} testid="report-top-sources" />
            </div>

            <h3 className="font-display font-bold text-zinc-950 mt-6 mb-2">Metric definitions</h3>
            <ul className="text-xs text-zinc-600 space-y-1 list-disc pl-4">
              <li>Unique visitors: distinct anonymised visitor hashes (IP + browser, one-way hashed) in the period.</li>
              <li>Impressions: a job card rendered in a results list. Views: a HiredToday job detail page opened.</li>
              <li>Outbound apply clicks: a visitor leaving HiredToday via the partner's tracked redirect. These are not completed applications, the application happens on the partner's website.</li>
              <li>Native applications: applications fully submitted on HiredToday (confirmed).</li>
              <li>Estimated revenue = unique outbound clicks × configured CPC. Confirmed revenue = figures entered manually from partner statements. No revenue is assumed or invented.</li>
            </ul>
            <h3 className="font-display font-bold text-zinc-950 mt-4 mb-2">Methodology & privacy</h3>
            <p className="text-xs text-zinc-600">All events are recorded server-side. Known bots/crawlers are filtered by user-agent, administrator clicks are excluded, and duplicate outbound clicks from the same visitor on the same job are counted once for unique figures. Reports contain no candidate names, emails or other personal information.</p>

            <button onClick={() => window.print()} data-testid="partner-report-print"
              className="mt-6 h-10 px-6 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 inline-flex items-center gap-1.5">
              <Printer className="h-3.5 w-3.5" /> Print / Save as PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
